import {styled} from '@mui/material/styles';
import {alpha, Chip, chipClasses, ChipProps} from '@mui/material';
import {ForwardedRef, forwardRef} from 'react';

import {shouldNotForwardProp} from 'utils/styled';

import Cross2Icon from 'components/icons/cross-2.svg';
import DropdownIcon from 'components/icons/dropdown.svg';
import {Skeleton} from 'components/Skeleton';

const STopbarFilterChip = styled(
  Chip,
  shouldNotForwardProp('chipVariant')
)<{
  chipVariant?: 'primary' | 'secondary';
}>(({theme, chipVariant, clickable}) => ({
  padding: theme.spacing(2, 3),
  borderRadius: theme.shape.borderRadius,

  ...(chipVariant === 'primary'
    ? {
        backgroundColor: theme.palette.action.hover,
        border: `1px solid ${theme.palette.primary.light}`,
        color: theme.palette.primary.main,

        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.light, theme.palette.action.focusOpacity),
        },
      }
    : {
        backgroundColor: theme.palette.background.white,
        border: `1px solid ${theme.palette.text.secondary}`,
      }),

  [`.${chipClasses.label}`]: {
    display: 'flex',
    alignItems: 'center',
  },

  [`.${chipClasses.deleteIcon}`]: {
    cursor: clickable ? 'pointer' : 'default',
  },
}));

const SDropdownWrapper = styled('span')(({theme}) => ({
  display: 'inline-flex',
  paddingLeft: theme.spacing(2),
}));

const SLabelWrapper = styled('span')(() => ({
  position: 'relative',
}));

const SLabelContent = styled(
  'span',
  shouldNotForwardProp('loading')
)<{loading?: boolean}>(({loading}) => ({
  opacity: loading ? 0 : 1,
}));

const SLabelSkeleton = styled(Skeleton)(() => ({
  position: 'absolute',
  width: '100%',
  top: 0,
  left: 0,
}));

type TopbarFilterChipProps = Omit<ChipProps, 'variant'> & {
  withDropdown?: boolean;
  loading?: boolean;
  variant?: 'primary' | 'secondary';
};

const TopbarFilterChipComponent = (
  {withDropdown, loading, label, variant = 'secondary', ...rest}: TopbarFilterChipProps,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const formattedLabel = (
    <SLabelWrapper>
      {loading && <SLabelSkeleton />}
      <SLabelContent loading={loading}>{label}</SLabelContent>
    </SLabelWrapper>
  );

  const handleDelete = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!loading) {
      rest.onDelete?.(event);
    }
  };

  return (
    <STopbarFilterChip
      {...rest}
      chipVariant={variant}
      ref={ref}
      onDelete={rest.onDelete ? handleDelete : undefined}
      onClick={!loading ? rest.onClick : undefined}
      deleteIcon={<Cross2Icon data-testid='delete-button' />}
      clickable={!rest.disabled && !loading}
      label={
        withDropdown ? (
          <>
            {formattedLabel}

            <SDropdownWrapper>
              <DropdownIcon />
            </SDropdownWrapper>
          </>
        ) : (
          formattedLabel
        )
      }
    />
  );
};

export const TopbarFilterChip = forwardRef(TopbarFilterChipComponent);
